import { useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import FormInput from '../../../components/forms/formInput';
import OverlayLoader from '../../../components/overlayLoader';
import SbrDrawer from '../../../components/sbr/SbrDrawer';
import { createBadge, updateBadge } from '../../../services/badges';
import { onMutateSuccess } from '../../../utils/queryResponse';
import { tagLabel } from '../../../constants/labels';

const TagIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
);

const initBadge = {
    length: 10,
}

/** "26-00432" + 4 → "26-00436" (preview of the last number a batch would issue). */
const previewCode = (next, count) => {
    if (!next?.year || !next?.seq || !count) return ''
    const seq = next.seq + Math.max(0, count - 1)
    return `${String(next.year).padStart(2, '0')}-${String(seq).padStart(5, '0')}`
}

const SetBadgeModal = ({
    display,
    toggle,
    selectedBadge,
    lastIssued,
}) => {

    const { t } = useTranslation();

    const [badge, setBadge] = useState(initBadge)

    const onShow = () => {
        !selectedBadge.id ? setBadge(initBadge) : setBadge(selectedBadge)
    }

    const onHide = () => {
        toggle(false)
    }

    const queryClient = useQueryClient()

    const { mutate, isLoading } = useMutation(selectedBadge.id ? updateBadge : createBadge, {
        onSuccess: onMutateSuccess(() => {
            queryClient.invalidateQueries(['badges'])
            queryClient.invalidateQueries(['available-badges'])
            onHide()
        })
    })

    const handleSubmit = (e) => {
        e.preventDefault()
        e.stopPropagation()
        mutate(selectedBadge.id ? { id: badge.id, badgeNo: badge.badgeNo } : { length: badge.length })
    }

    const handleChange = (objValue) => {
        const newBadge = { ...badge, ...objValue }
        setBadge(newBadge)
    }

    const next = lastIssued?.next
    const count = Number(badge.length) || 0

    return (
        <SbrDrawer
            show={display}
            onHide={onHide}
            onShow={onShow}
            secondary
            backdrop={isLoading ? 'static' : true}
            title={selectedBadge.id ? t('settingsPage.editTagTitle') : t('settingsPage.issueTagsTitle')}
            subtitle={selectedBadge.id ? t('settingsPage.editTagSubtitle') : t('settingsPage.issueTagsSubtitle')}
            icon={<TagIcon />}
            footer={
                <>
                    <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={onHide} disabled={isLoading}>{t('common.cancel')}</button>
                    <button type='submit' form="set-badge" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isLoading}>
                        {selectedBadge.id
                            ? (isLoading ? t('settingsPage.editing') : t('common.edit'))
                            : (isLoading ? t('settingsPage.issuing') : (count === 1 ? t('settingsPage.issueCount', { count }) : t('settingsPage.issueCountPlural', { count: count || '' })))}
                    </button>
                </>
            }
        >
            <Form id='set-badge' onSubmit={handleSubmit} style={{ position: 'relative' }}>
                <Row className="mb-3">
                    <Col lg="12">
                        {selectedBadge.id ?
                            <FormInput className="form-control mb-2" label={t('settingsPage.tagNo')} type="number" value={badge.badgeNo ?? ''}
                                onChange={(e) => handleChange({ badgeNo: parseInt(e.target.value) })} required
                            />
                            :
                            <>
                                <FormInput className="form-control mb-2" label={t('settingsPage.howManyTags')} type="number" min={1} max={1000} value={badge.length ?? ''}
                                    onChange={(e) => handleChange({ length: parseInt(e.target.value) })} required
                                />
                                {lastIssued && (
                                    <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                                        {t('settingsPage.lastIssued')} <b>{lastIssued.last ? tagLabel(lastIssued.last) : '—'}</b>
                                        {' · '}{t('settingsPage.next')} <b>{next?.code}</b>
                                        {count > 0 && next?.code && <> · {t('settingsPage.thisBatch')} <b>{next.code}</b>{count > 1 && <> – <b>{previewCode(next, count)}</b></>}</>}
                                    </div>
                                )}
                            </>
                        }
                    </Col>
                </Row>

                {isLoading &&
                    <OverlayLoader />
                }

            </Form>
        </SbrDrawer>
    )
}

export default SetBadgeModal
